import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import styled from 'styled-components';

// @ts-ignore
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const Card = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 0.25rem;
  background: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const CardBody = styled.div`
  padding: 0.5rem;
`;

const ChartContainer = styled.div`
  height: 210px;
  width: 100%;
  position: relative;

  @media (max-width: 768px) {
    height: 190px;
  }

  @media (max-width: 576px) {
    height: 180px;
  }
`;

interface ChartProps {
  data: any;
  contractType: string;
}

const Chart: React.FC<ChartProps> = ({ data, contractType }) => {
  const getChartConfig = () => {

    switch (contractType) {
      case '0': // Even/Odd
        return {
          labels: ['Even', 'Odd'],
          datasets: [{
            data: data?.datasets?.[0]?.data || [0, 0],
            backgroundColor: ['#10b981', '#ef4444'],
            borderColor: ['#10b981', '#ef4444'],
            borderWidth: 1,
            borderRadius: 5,
          }],
        };

      case '1': // Rise/Fall
        return {
          labels: ['Rise', 'Fall'],
          datasets: [{
            data: data?.datasets?.[0]?.data || [0, 0],
            backgroundColor: ['#10b981', '#ef4444'],
            borderColor: ['#10b981', '#ef4444'],
            borderWidth: 1,
            borderRadius: 5,
          }],
        };

      case '2': // Over/Under
        return {
          labels: ['Over', 'Under'],
          datasets: [{
            data: data?.datasets?.[0]?.data || [0, 0],
            backgroundColor: ['#10b981', '#ef4444'],
            borderColor: ['#10b981', '#ef4444'],
            borderWidth: 1,
            borderRadius: 5,
          }],
        };

      case '3': // Digit Frequency
        return {
          labels: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'],
          datasets: [{
            data: data?.datasets?.[0]?.data || Array(10).fill(0),
            backgroundColor: data?.datasets?.[0]?.backgroundColor || Array(10).fill('rgba(59, 130, 246, 0.8)'),
            borderColor: data?.datasets?.[0]?.borderColor || Array(10).fill('rgba(59, 130, 246, 1)'),
            borderWidth: 1,
            borderRadius: 5,
          }],
        };

      default:
        return {
          labels: ['Even', 'Odd'],
          datasets: [{
            data: [0, 0],
            backgroundColor: ['rgba(59, 130, 246, 0.8)', 'rgba(239, 68, 68, 0.8)'],
            borderColor: ['rgba(59, 130, 246, 1)', 'rgba(239, 68, 68, 1)'],
            borderWidth: 1,
            borderRadius: 5,
          }],
        };
    }
  };

  const options = {
    indexAxis: (contractType === '3' ? (window.innerWidth >= 768 ? 'x' : 'y') : 'y') as 'x' | 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            return `${context.dataset.label || context.label}: ${context.raw}%`;
          }
        }
      },
      datalabels: {
        display: true,
        color: '#000000',
        font: {
          weight: 'bold' as const,
          size: 12
        },
        formatter: (value: number) => `${value}%`,
        anchor: 'center' as const,
        align: 'center' as const
      }
    },
    scales: {
      x: {
        display: contractType === '3' && window.innerWidth >= 768,
        grid: { display: false },
        max: contractType === '3' ? undefined : 100
      },
      y: {
        display: contractType === '3' ? window.innerWidth < 768 : true,
        grid: { display: false },
        max: contractType === '3' ? undefined : 100
      }
    }
  };

  return (
    <Card>
      <CardBody>
        <ChartContainer>
          <Bar data={getChartConfig()} options={options} />
        </ChartContainer>
      </CardBody>
    </Card>
  );
};

export default Chart;
